/**
 * Active weather along a planned line — NWS alerts and conditions, not a model.
 *
 * The events scan is a web search for scheduled disruptions. This one asks the
 * same area-intel service the COP toolbar uses, for the route's bounding box.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import areaIntelService, { WeatherAlert, WeatherData } from '../../services/areaIntelService';
import { PlannerState } from './types';

export interface RouteWeatherReport {
  conditions?: WeatherData | null;
  alerts: WeatherAlert[];
  captured_at?: string;
}

export interface UseRouteWeather {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  report: RouteWeatherReport | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
  capturedAt: string | null;
  refresh: () => void;
}

function boundsOf(coords: number[][]) {
  let minLat = 90;
  let maxLat = -90;
  let minLon = 180;
  let maxLon = -180;
  for (const pair of coords) {
    const lon = Number(pair[0]);
    const lat = Number(pair[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  const pad = 0.08;
  return {
    north: maxLat + pad,
    south: minLat - pad,
    east: maxLon + pad,
    west: minLon - pad,
  };
}

export function useRouteWeather(state: PlannerState): UseRouteWeather {
  const [enabled, setEnabled] = useState(false);
  const [report, setReport] = useState<RouteWeatherReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [fetchedKey, setFetchedKey] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const fetched = useRef<{ key: string; nonce: number } | null>(null);

  const primary = state.routes[state.primaryIndex] || null;
  const coords = (primary?.geometry?.coordinates || []) as number[][];
  const geometryKey = useMemo(() => {
    if (coords.length < 2) return '';
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${coords.length}:${first[0].toFixed(4)},${first[1].toFixed(4)}:${last[0].toFixed(4)},${last[1].toFixed(4)}`;
  }, [coords]);

  useEffect(() => {
    if (!enabled || !geometryKey) return;
    const seen = fetched.current;
    if (seen && seen.key === geometryKey && seen.nonce === nonce) return;
    fetched.current = { key: geometryKey, nonce };
    let cancelled = false;
    setLoading(true);
    setError(null);
    areaIntelService.getAreaAlerts(boundsOf(coords), { includeWeather: true })
      .then((data) => {
        if (cancelled) return;
        const at = new Date().toISOString();
        setReport({
          conditions: data.weather || null,
          alerts: data.weather_alerts || [],
          captured_at: at,
        });
        setFetchedKey(geometryKey);
        setCapturedAt(at);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.message || 'Could not read weather for this route');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [enabled, geometryKey, nonce, coords]);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  return {
    enabled,
    setEnabled,
    report,
    loading,
    error,
    stale: !!report && !!fetchedKey && fetchedKey !== geometryKey,
    capturedAt,
    refresh,
  };
}
